import type { Student } from "../providers/studentsProvider";
import { DialogProps } from "../misc/DialogProps";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
} from "@mui/material";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { Datagrid, FunctionField, List, TextField, useRecordContext } from "react-admin";
import { EmptyState } from "../misc/EmptyState";
import { ChipsField } from "../fields/ChipsField";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapIcon from "@mui/icons-material/SwapHoriz";
import CancelIcon from "@mui/icons-material/Cancel";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import * as React from "react";
import { cloneElement } from "react";
import { useSetRecoilState } from "recoil";
import { bookedQuoteDialogInitialState, bookedQuoteShownInBookedQuoteDialogState } from "./BookedQuoteDialog";
import { BookedQuote } from "../providers/bookedQuotesProvider";
import { grants } from "../backoffice.access_control";
import { BookedTraining } from "../model/BookedTraining";
import { useMenu } from "../hooks/useMenu";
import { CancelBookedTrainingDialog } from "./CancelBookedTrainingDialog";
import { ReactivateCancelledBookedTrainingDialog } from "./ReactivateCancelledBookedTrainingDialog";
import { useDialog } from "../hooks/useDialog";
import { EuroIcon } from "../icons/EuroIcon";
import { LicenseIcon } from "../icons/LicenseIcon";

export function BookedQuotesDialog(props: { student: Student; addBundleButton: React.ReactElement } & DialogProps) {
  const { student, addBundleButton, ...dialogProps } = props;
  const closeDialog = dialogProps.onClose;
  const setBookedQuoteShownInDialog = useSetRecoilState(bookedQuoteShownInBookedQuoteDialogState);
  const setBookedQuoteDialogInitialState = useSetRecoilState(bookedQuoteDialogInitialState);
  const [bookedTrainingToCancel, setBookedTrainingToCancel] = React.useState<BookedTraining>();
  const [bookedTrainingToReactivate, setBookedTrainingToReactivate] = React.useState<BookedTraining>();
  const { dialogProps: cancelDialogProps, openDialog: openCancelDialog } = useDialog();
  const { dialogProps: reactivateDialogProps, openDialog: openReactivateDialog } = useDialog();

  return (
    <>
      <Dialog {...dialogProps} maxWidth="md">
        <DialogTitle>Gebuchte Pakete</DialogTitle>
        <DialogCloseButton onClick={closeDialog} />
        <DialogContent>
          <List
            resource="bookedQuotes"
            filter={{ studentId: student.id }}
            sort={{ field: "statusSortOrder", order: "ASC" }}
            exporter={false}
            pagination={false}
            empty={<EmptyState />}
          >
            <Datagrid
              bulkActionButtons={false}
              empty={<EmptyState noEntriesText="Noch keine Pakete gebucht." />}
              sx={{
                "& .MuiTableRow-root.MuiTableRow-hover:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <FunctionField<BookedQuote>
                label="Status"
                render={(bookedQuote) =>
                  bookedQuote.isCancelled ? "Abgebrochen" : bookedQuote.isFinished ? "Fertig" : "Aktiv"
                }
              />
              <TextField label="Name" source="bundleName" />
              <ChipsField label="Klasse" source="drivingLicenseClasses" />
              <BookedQuoteActions
                student={student}
                onShowPrices={(bookedQuote) => {
                  setBookedQuoteDialogInitialState("prices overview");
                  setBookedQuoteShownInDialog(bookedQuote);
                }}
                onChangeDrivingLicenseClass={(bookedQuote) => {
                  setBookedQuoteDialogInitialState("change driving license class");
                  setBookedQuoteShownInDialog(bookedQuote);
                }}
                onReplaceBundle={(bookedQuote) => {
                  setBookedQuoteDialogInitialState("replace bundle");
                  setBookedQuoteShownInDialog(bookedQuote);
                }}
                onCancelBookedTraining={(training) => {
                  setBookedTrainingToCancel(training);
                  openCancelDialog();
                }}
                onReactivateBookedTraining={(training) => {
                  setBookedTrainingToReactivate(training);
                  openReactivateDialog();
                }}
              />
            </Datagrid>
          </List>
        </DialogContent>
        {(grants.has("Quote:book") || grants.has("changeBundle")) && (
          <DialogActions>
            {cloneElement(addBundleButton, {
              onClick: () => {
                addBundleButton.props.onClick();
              },
            })}
          </DialogActions>
        )}
      </Dialog>
      {bookedTrainingToCancel && (
        <CancelBookedTrainingDialog
          student={student}
          bookedTraining={bookedTrainingToCancel}
          {...cancelDialogProps}
          onClose={() => {
            cancelDialogProps.onClose();
            closeDialog();
          }}
        />
      )}
      {bookedTrainingToReactivate && (
        <ReactivateCancelledBookedTrainingDialog
          student={student}
          bookedTraining={bookedTrainingToReactivate}
          {...reactivateDialogProps}
          onClose={() => {
            reactivateDialogProps.onClose();
            closeDialog();
          }}
        />
      )}
    </>
  );
}

function BookedQuoteActions({
  student,
  onShowPrices,
  onChangeDrivingLicenseClass,
  onReplaceBundle,
  onCancelBookedTraining,
  onReactivateBookedTraining,
}: {
  student: Student;
  onShowPrices: (bookedQuote: BookedQuote) => void;
  onChangeDrivingLicenseClass: (bookedQuote: BookedQuote) => void;
  onReplaceBundle: (bookedQuote: BookedQuote) => void;
  onCancelBookedTraining: (bookedTraining: BookedTraining) => void;
  onReactivateBookedTraining: (bookedTraining: BookedTraining) => void;
}) {
  const bookedQuote = useRecordContext<BookedQuote>();
  const { anchorEl, open, openMenu, closeMenu } = useMenu();

  if (!bookedQuote) {
    return null;
  }

  const bookedTrainingsForDrivingLicense = student.bookedTrainings.filter(
    (it) => bookedQuote.bookedTrainingIds.includes(it.id) && it.isForDrivingLicense,
  );
  const cancellableBookedTrainings = student.bookedTrainings.filter(
    (it) => bookedQuote.bookedTrainingIds.includes(it.id) && !it.isFinished && !it.isCancelled,
  );
  const cancelledBookedTrainings = student.bookedTrainings.filter(
    (it) => bookedQuote.bookedTrainingIds.includes(it.id) && it.isCancelled,
  );
  return (
    <>
      <IconButton
        aria-label={`Aktionen für Paket ${bookedQuote.bundleName}`}
        onClick={(event) => {
          event.stopPropagation();
          openMenu(event);
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            closeMenu();
            onShowPrices(bookedQuote);
          }}
        >
          <ListItemIcon>
            <EuroIcon />
          </ListItemIcon>
          Preise anzeigen & bearbeiten
        </MenuItem>
        {bookedTrainingsForDrivingLicense.length > 0 && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              closeMenu();
              onChangeDrivingLicenseClass(bookedQuote);
            }}
          >
            <ListItemIcon>
              <LicenseIcon />
            </ListItemIcon>
            {bookedTrainingsForDrivingLicense.length === 1 ? "Führerscheinklasse ändern" : "Führerscheinklassen ändern"}
          </MenuItem>
        )}
        {(grants.has("Quote:book") || grants.has("changeBundle")) && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              closeMenu();
              onReplaceBundle(bookedQuote);
            }}
          >
            <ListItemIcon>
              <SwapIcon />
            </ListItemIcon>
            Paket ersetzen
          </MenuItem>
        )}
        {grants.has("Student:cancelBookedTraining") &&
          cancellableBookedTrainings.length > 0 &&
          cancellableBookedTrainings.map((bookedTraining) => (
            <MenuItem
              key={bookedTraining.id}
              onClick={(event) => {
                event.stopPropagation();
                closeMenu();
                onCancelBookedTraining(bookedTraining);
              }}
            >
              <ListItemIcon>
                <CancelIcon />
              </ListItemIcon>
              {bookedQuote.bookedTrainingIds.length === 1
                ? "Ausbildung abbrechen"
                : `Ausbildung für Klasse ${bookedTraining.shortLabel} abbrechen`}
            </MenuItem>
          ))}
        {grants.has("Student:cancelBookedTraining") &&
          cancelledBookedTrainings.length > 0 &&
          cancelledBookedTrainings.map((training) => (
            <MenuItem
              key={training.id}
              onClick={(event) => {
                event.stopPropagation();
                closeMenu();
                onReactivateBookedTraining(training);
              }}
            >
              <ListItemIcon>
                <RestartAltIcon />
              </ListItemIcon>
              {bookedQuote.bookedTrainingIds.length === 1
                ? "Ausbildung reaktivieren"
                : `Ausbildung für Klasse ${training.shortLabel} reaktivieren`}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
}
